import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Select, MenuItem, FormControl,
  Grid, Paper, CircularProgress, Checkbox, ListItemText,
  OutlinedInput
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend
} from 'chart.js';
import authService from '../../api-authorization/AuthorizeService';
import styles from './AdminSummaryDashboard.module.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function StatCard({ label, value, unit }) {
  return (
    <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight="bold">
        {value != null ? Number(value).toLocaleString() : '-'}
        {unit && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>{unit}</Typography>}
      </Typography>
    </Paper>
  );
}

const AdminSummaryDashboard = () => {
  const defaultStart = dayjs().startOf('year');
  const defaultEnd = dayjs();

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [clients, setClients] = useState([]);
  const [crops, setCrops] = useState([]);
  const [selectedCrops, setSelectedCrops] = useState([]);
  const [cropsLoading, setCropsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [clientMonthlyStats, setClientMonthlyStats] = useState([]);
  const [cropMonthlyStats, setCropMonthlyStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const token = await authService.getAccessToken();
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}/api/admin/clients/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setClients(data);
          setSelectedClientIds(data.map(c => c.id));
        }
      } catch { /* non-critical */ }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) return;
    if (selectedClientIds.length === 0) {
      setCrops([]);
      setSelectedCrops([]);
      return;
    }
    const fetchCrops = async () => {
      setCropsLoading(true);
      try {
        const token = await authService.getAccessToken();
        const base = process.env.REACT_APP_BASE_URL;
        const dateParams = `startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
        const clientParams = '&' + selectedClientIds.map(id => `clientIds=${id}`).join('&');
        const res = await fetch(`${base}/api/summarydashboard/crops?${dateParams}${clientParams}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCrops(data);
          setSelectedCrops(data);
        }
      } catch { /* non-critical */ } finally {
        setCropsLoading(false);
      }
    };
    fetchCrops();
  }, [startDate, endDate, selectedClientIds]);

  useEffect(() => {
    if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) return;
    if (selectedClientIds.length === 0) {
      setStats(null);
      setMonthlyStats([]);
      setClientMonthlyStats([]);
      setCropMonthlyStats([]);
      return;
    }
    if (selectedCrops.length === 0) {
      setStats(null);
      setMonthlyStats([]);
      setClientMonthlyStats([]);
      setCropMonthlyStats([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await authService.getAccessToken();
        const headers = { Authorization: `Bearer ${token}` };
        const base = process.env.REACT_APP_BASE_URL;
        const dateParams = `startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
        const clientParams = selectedClientIds.length > 0
          ? '&' + selectedClientIds.map(id => `clientIds=${id}`).join('&')
          : '';
        const cropParams = selectedCrops.length > 0 && selectedCrops.length < crops.length
          ? '&' + selectedCrops.map(c => `crops=${encodeURIComponent(c)}`).join('&')
          : '';

        const [statsRes, monthlyRes, clientMonthlyRes, cropMonthlyRes] = await Promise.all([
          fetch(`${base}/api/summarydashboard/stats?${dateParams}${clientParams}${cropParams}`, { headers }),
          fetch(`${base}/api/summarydashboard/monthly?${dateParams}${clientParams}${cropParams}`, { headers }),
          fetch(`${base}/api/summarydashboard/monthly-by-client?${dateParams}${clientParams}${cropParams}`, { headers }),
          fetch(`${base}/api/summarydashboard/monthly-by-crop?${dateParams}${clientParams}${cropParams}`, { headers })
        ]);

        if (!statsRes.ok || !monthlyRes.ok || !clientMonthlyRes.ok || !cropMonthlyRes.ok) throw new Error('Failed to fetch dashboard data.');

        const [yearly, monthly, clientMonthly, cropMonthly] = await Promise.all([statsRes.json(), monthlyRes.json(), clientMonthlyRes.json(), cropMonthlyRes.json()]);
        setStats(yearly);
        setMonthlyStats(monthly);
        setClientMonthlyStats(clientMonthly);
        setCropMonthlyStats(cropMonthly);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, selectedClientIds, selectedCrops]);

  const allSelected = clients.length > 0 && selectedClientIds.length === clients.length;
  const allCropsSelected = crops.length > 0 && selectedCrops.length === crops.length;

  const handleClientChange = (event) => {
    const value = event.target.value;
    if (value.includes('__all__')) {
      setSelectedClientIds(allSelected ? [] : clients.map(c => c.id));
    } else {
      setSelectedClientIds(value);
    }
  };

  const handleCropChange = (event) => {
    const value = event.target.value;
    if (value.includes('__all_crops__')) {
      setSelectedCrops(allCropsSelected ? [] : crops);
    } else {
      setSelectedCrops(value);
    }
  };

  const chartLabels = useMemo(() => {
    return monthlyStats.map(m => {
      const monthName = MONTH_NAMES[m.month - 1];
      return monthlyStats.some(r => r.year !== m.year) ? `${monthName} ${m.year}` : monthName;
    });
  }, [monthlyStats]);

  const productChartData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Product Applied',
        data: monthlyStats.map(m => m.totalPesticideSprayedGallons ?? 0),
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25,118,210,0.1)',
        tension: 0.3,
        pointRadius: 3,
      },
      {
        label: 'Product Saved',
        data: monthlyStats.map(m => m.totalGallonsSaved ?? 0),
        borderColor: '#43a047',
        backgroundColor: 'rgba(67,160,71,0.1)',
        tension: 0.3,
        pointRadius: 3,
      },
    ],
  };

  const acresChartData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Coverage Acres',
        data: monthlyStats.map(m => m.totalCoverageAreaAcres ?? 0),
        borderColor: '#ff9800',
        backgroundColor: 'rgba(255,152,0,0.1)',
        tension: 0.3,
        pointRadius: 3,
      },
      {
        label: 'Total Acres',
        data: monthlyStats.map(m => m.totalAreaAcres ?? 0),
        borderColor: '#9c27b0',
        backgroundColor: 'rgba(156,39,176,0.1)',
        tension: 0.3,
        pointRadius: 3,
      },
    ],
  };

  const chartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } } };

  const CLIENT_COLORS = [
    '#1976d2', '#43a047', '#ff9800', '#9c27b0', '#e53935',
    '#00acc1', '#f06292', '#8d6e63', '#546e7a', '#fdd835',
  ];

  const clientNames = [...new Set(clientMonthlyStats.map(r => r.clientName))].sort();
  const clientSavedChartData = {
    labels: chartLabels,
    datasets: clientNames.map((name, i) => ({
      label: name,
      data: monthlyStats.map(m => {
        const row = clientMonthlyStats.find(r => r.clientName === name && r.month === m.month && r.year === m.year);
        return row ? Number(row.totalGallonsSaved) : 0;
      }),
      borderColor: CLIENT_COLORS[i % CLIENT_COLORS.length],
      backgroundColor: CLIENT_COLORS[i % CLIENT_COLORS.length] + '1a',
      tension: 0.3,
      pointRadius: 3,
    })),
  };

  const cropNames = [...new Set(cropMonthlyStats.map(r => r.crop))].sort();
  const cropSavedChartData = {
    labels: chartLabels,
    datasets: cropNames.map((crop, i) => ({
      label: crop,
      data: monthlyStats.map(m => {
        const row = cropMonthlyStats.find(r => r.crop === crop && r.month === m.month && r.year === m.year);
        return row ? Number(row.totalGallonsSaved) : 0;
      }),
      borderColor: CLIENT_COLORS[i % CLIENT_COLORS.length],
      backgroundColor: CLIENT_COLORS[i % CLIENT_COLORS.length] + '1a',
      tension: 0.3,
      pointRadius: 3,
    })),
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className={styles.container} sx={{ pb: { xs: 3, sm: 2 } }}>
        <Typography variant="h4" sx={{ pb: 2, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Summary Dashboard</Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Clients</Typography>
            <FormControl size="small" sx={{ minWidth: 220, maxWidth: 340 }}>
              <Select
                multiple
                displayEmpty
                value={selectedClientIds}
                onChange={handleClientChange}
                input={<OutlinedInput />}
                renderValue={(selected) => {
                  if (selected.length === 0) return <em>None Selected</em>;
                  if (selected.length === clients.length) return 'All Clients';
                  return clients
                    .filter(c => selected.includes(c.id))
                    .map(c => c.name)
                    .join(', ');
                }}
              >
                <MenuItem value="__all__">
                  <Checkbox checked={allSelected} indeterminate={selectedClientIds.length > 0 && !allSelected} />
                  <ListItemText primary={allSelected ? 'Deselect All' : 'Select All'} />
                </MenuItem>
                {clients.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    <Checkbox checked={selectedClientIds.includes(c.id)} />
                    <ListItemText primary={c.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {crops.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Crops</Typography>
              <FormControl size="small" sx={{ minWidth: 180, maxWidth: 300 }} disabled={cropsLoading}>
                <Select
                  multiple
                  displayEmpty
                  value={selectedCrops}
                  onChange={handleCropChange}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) return <em>None Selected</em>;
                    if (selected.length === crops.length) return 'All Crops';
                    return selected.join(', ');
                  }}
                >
                  <MenuItem value="__all_crops__">
                    <Checkbox checked={allCropsSelected} indeterminate={selectedCrops.length > 0 && !allCropsSelected} />
                    <ListItemText primary={allCropsSelected ? 'Deselect All' : 'Select All'} />
                  </MenuItem>
                  {crops.map(crop => (
                    <MenuItem key={crop} value={crop}>
                      <Checkbox checked={selectedCrops.includes(crop)} />
                      <ListItemText primary={crop} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Start Date</Typography>
            <DatePicker
              value={startDate}
              onChange={val => val && setStartDate(val)}
              maxDate={endDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>End Date</Typography>
            <DatePicker
              value={endDate}
              onChange={val => val && setEndDate(val)}
              minDate={startDate}
              maxDate={dayjs()}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
          </Box>
        </Box>

        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>}
        {error && <Typography color="error">{error}</Typography>}

        {!loading && stats && (
          <>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <StatCard label="Product Applied" value={stats.totalPesticideSprayedGallons} unit="gal" />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <StatCard label="Product Saved" value={stats.totalGallonsSaved} unit="gal" />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <StatCard label="Coverage Area" value={stats.totalCoverageAreaAcres} unit="ac" />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <StatCard label="Total Area" value={stats.totalAreaAcres} unit="ac" />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <StatCard label="Active Sprayers" value={stats.distinctTractorCount} />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <StatCard label="Active Clients" value={stats.distinctClientCount} />
              </Grid>
            </Grid>

            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Monthly Trends — {startDate.format('MMM D, YYYY')} to {endDate.format('MMM D, YYYY')}
            </Typography>

            {clientSavedChartData.datasets.length > 0 && (
              <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Product Saved by Client (Monthly)</Typography>
                <Box sx={{ position: 'relative', height: 300 }}>
                  <Line data={clientSavedChartData} options={chartOptions} />
                </Box>
              </Paper>
            )}

            {cropSavedChartData.datasets.length > 0 && (
              <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Product Saved by Crop (Monthly)</Typography>
                <Box sx={{ position: 'relative', height: 300 }}>
                  <Line data={cropSavedChartData} options={chartOptions} />
                </Box>
              </Paper>
            )}

            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Product Applied vs. Saved</Typography>
              <Box sx={{ position: 'relative', height: 300 }}>
                <Line data={productChartData} options={chartOptions} />
              </Box>
            </Paper>

            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Coverage Area vs. Total Area (acres)</Typography>
              <Box sx={{ position: 'relative', height: 300 }}>
                <Line data={acresChartData} options={chartOptions} />
              </Box>
            </Paper>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default AdminSummaryDashboard;
